import React, {
  useState, useEffect, useContext, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  Button, TextField, Select, MenuItem,
} from '@mui/material';
import AppContext from '../context';
import Table from '../components/Table';
// eslint-disable-next-line no-unused-vars
import {
  getLocalStorageTablePref, getRowIdxForId, updateTablePreferenceLocalStorage,
  tableColumnPreferenceLoader,
} from '../utils';
import {
  addFavoriteClient, checkIfFavorite, getFavoriteClients, removeFavoriteClient,
} from '../utils/favsAndRecentClients';
import FavButton from '../components/FavButton';
import SettingsButton from '../components/SettingsButton';
import { clientColumns } from '../utils/schemaInfo';

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const initColumnVisibilityModel = {
  adsconfiguration: false,
  bingids: false,
  campaignmonitorlocation: false,
  classyorgid: false,
  codedicurl: false,
  curateautoupload: false,
  curatedays: false,
  curatefrequency: false,
  curatesubscriber: false,
  donorfloortype: false,
  donorfloorvalue: false,
  emailconfigurations: false,
  excludematchqualifier: false,
  expandedwindow: false,
  facebookids: false,
  facebookloc: false,
  fiscalyearstart: false,
  googleids: false,
  goopid: false,
  hubspotstitchintegrationname: false,
  hubsrcpath: false,
  inboxmonsterrule: false,
  isadsreportingactive: true,
  isadvantagemembertimestamp: false,
  identity: false,
  islowinventory: false,
  mailchimpdir: false,
  mmclients: false,
  mmrequired: false,
  mmmlrequired: false,
  mmsmsclients: false,
  mmmlclients: false,
  ngpactbluemanualcodes: false,
  ngpeadb: false,
  ngpeaserver: false,
  notes: false,
  numeroaccountids: false,
  outbrainids: false,
  pardotloc: false,
  pausedata: false,
  reactivationconfigurations: false,
  salesforceloc: false,
  salsakey: false,
  sector: false,
  sfgid: false,
  sharecapmaxshares: false,
  sharecapmonths: false,
  slackchannel: false,
  smsconfigurations: false,
  smsshortcodedatasource: false,
  smslongcodedatasource: false,
  stwaccountids: false,
  stwnoncoopsubaccountids: false,
  switchboardshortcodedataset: false,
  switchboardlongcodedataset: false,
  tatangoids: false,
  timezone: false,
  totemid: false,
  twitterids: false,
  vendor: false,
  updatedat: false,
  createdat: false,
};

const defaultSortModel = [{ field: 'code', sort: 'asc' }];

function ClientList(props) {
  const {
    clients,
    isLoading,
    setHeader,
    hasTDCAdminPermission,
    hasDataEngineerPermission,
    hasAnalystPermission,
    view = 'clientlist',
    handleViewChange,
  } = props;
  const navigate = useNavigate();
  const { setFavClients } = useContext(AppContext);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    initColumnVisibilityModel,
  );
  const [pageState, setPageState] = useState({
    isLoading: true,
    page: 0, // mui pages are 0 indexed
    pageSize: 50,
    data: [],
    allClients: [],
    total: 0,
    favoriteClients: [],
  });
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState(defaultSortModel);

  const pageSetter = setHeader;
  // REFACTOR temp workaround to restrict client create/edit permissions
  const hasClientCreateEditPermission = hasTDCAdminPermission;

  const prevData = useRef(pageState.data.concat(pageState.favoriteClients));
  useEffect(() => {
    prevData.current = pageState.data.concat(pageState.favoriteClients);
  }, [pageState.data.concat(pageState.favoriteClients)]);

  const handleToggleFav = (event, id) => {
    let realId;
    if (event.row) {
      realId = event.id;
    } else {
      event.stopPropagation();
      realId = id;
    }
    const currData = prevData.current;
    const idx = getRowIdxForId(realId, currData);
    const selectedClient = currData[idx].code;
    const isFav = checkIfFavorite(selectedClient);
    if (isFav) {
      removeFavoriteClient(selectedClient);
    } else {
      addFavoriteClient(selectedClient);
    }
    const newFavs = getFavoriteClients();
    setFavClients(newFavs);
    setPageState((old) => {
      const allRows = old.allClients.concat(old.favoriteClients);
      const newPinnedRows = allRows.filter((row) => newFavs.includes(row.code));
      const newFormedRows = allRows.filter((row) => !newFavs.includes(row.code));
      return {
        ...old,
        data: newFormedRows,
        allClients: newFormedRows,
        favoriteClients: newPinnedRows,
      };
    });
  };

  const actionCol = [
    {
      field: 'favorite',
      type: 'actions',
      headerName: 'Favorite',
      disableClickEventBubbling: true,
      cellClassName: 'favorite',
      getActions: ({ id }) => [
        <FavButton
          key="favorite"
          toggleFav={handleToggleFav}
          id={id}
          data={prevData.current}
        />,
      ],
    },
  ];

  if (hasTDCAdminPermission || hasDataEngineerPermission || hasAnalystPermission) {
    actionCol.push({
      field: 'settings',
      type: 'actions',
      headerName: 'Edit',
      disableClickEventBubbling: true,
      cellClassName: 'settings',
      getActions: ({ id }) => [
        <SettingsButton
          key="settings"
          id={id}
          data={prevData.current}
        />,
      ],
    });
  }

  const columns = actionCol.concat(clientColumns);

  const [orderedColumns, setOrderedColumns] = useState(columns);

  const customSetOrdered = (neworder) => {
    updateTablePreferenceLocalStorage('clientlist', 'column', neworder);
  };

  useEffect(() => {
    document.title = 'MissionPortal | Client List';
    const clientListFilters = getLocalStorageTablePref('clientlist', 'filter');
    if (clientListFilters) {
      setFilterModel(clientListFilters);
    }
    const clientListSortOrder = getLocalStorageTablePref('clientlist', 'sort');
    if (!clientListSortOrder) {
      updateTablePreferenceLocalStorage('clientlist', 'sort', defaultSortModel);
    } else {
      setSortModel(clientListSortOrder);
    }
    const clientListColumnVisibility = getLocalStorageTablePref('clientlist', 'column-visibility');
    if (!clientListColumnVisibility) {
      updateTablePreferenceLocalStorage('clientlist', 'column-visibility', initColumnVisibilityModel);
    } else {
      setColumnVisibilityModel(clientListColumnVisibility);
    }
  }, []);

  useEffect(() => {
    tableColumnPreferenceLoader('clientlist', 'column', columns, setOrderedColumns);
  }, [pageState]);

  useEffect(() => {
    if (typeof pageSetter === 'function') {
      pageSetter(['Client List']);
    }
  }, [pageSetter]);

  useEffect(() => {
    const favorites = getFavoriteClients();
    const pinnedRows = clients.filter((row) => favorites.includes(row.code));
    const formedRows = clients.filter((row) => !favorites.includes(row.code));
    setPageState((old) => ({
      ...old,
      isLoading,
      data: formedRows,
      allClients: formedRows,
      total: formedRows.length,
      favoriteClients: pinnedRows,
    }));
  }, [clients, isLoading]);

  const requestSearch = (searchValue) => {
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i');
    // eslint-disable-next-line max-len
    const filteredRows = pageState.allClients.filter((row) => Object.keys(row).some((field) => searchRegex.test(row[field])));
    setPageState((old) => ({
      ...old,
      total: filteredRows.length,
      data: filteredRows,
    }));
  };

  const handleRowClick = (event) => {
    const idx = getRowIdxForId(event.row.id, prevData.current);
    navigate(`/client/${event.row.code}`, {
      replace: false,
      state: { client: prevData.current[idx] },
    });
  };

  const getDefaultColumns = () => columns;

  return (
    <div data-testid="list">
      <div id="clientlist-header">
        <div className="header-left">
          <h2>Client List</h2>
          {hasClientCreateEditPermission && (
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                navigate('/newclient');
              }}
            >
              Add New Client
            </Button>
          )}
        </div>
        <div className="header-right">
          <Select
            value={view}
            onChange={handleViewChange}
            size="small"
          >
            <MenuItem value="clientlist">Client List</MenuItem>
            <MenuItem value="clientidentities">Client Identities</MenuItem>
            <MenuItem value="deliverysettings">Delivery Settings</MenuItem>
          </Select>
          {view === 'clientlist' && (
          <TextField
            id="outlined-basic"
            label="Search"
            variant="outlined"
            onChange={(event) => requestSearch(event.target.value)}
            size="small"
          />
          )}
        </div>
      </div>
      <Table
        rowClickFunc={handleRowClick}
        pageState={pageState}
        updatePageState={setPageState}
        columns={orderedColumns}
        sortModel={sortModel}
        setSortModel={setSortModel}
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        pinnedColumns={{ pinnedColumns: { left: ['settings', 'favorite'] } }}
        tablePrefix="clientlist"
        setOrderedColumns={setOrderedColumns}
        getDefaultColumns={getDefaultColumns}
        customSetOrdered={customSetOrdered}
      />
    </div>
  );
}

export default ClientList;

ClientList.propTypes = {
  clients: PropTypes.oneOfType([PropTypes.object]),
  isLoading: PropTypes.bool,
  setHeader: PropTypes.func,
  hasTDCAdminPermission: PropTypes.bool,
  hasDataEngineerPermission: PropTypes.bool,
  hasAnalystPermission: PropTypes.bool,
  view: PropTypes.string,
  handleViewChange: PropTypes.func,
};
